import { listExamsMetadata } from "@/graphql/customQueries";
import { getExam, listExams } from "@/graphql/queries";
import { Exam } from "@/graphql/schema";
import { getFeaturedCategories, groupExamsByCategory, groupExamsByType } from "@/hooks/exams/utils";
import { fetchSubjectsByCategory } from "@/hooks/subjects/utils";
import { platform } from "@/platform";
import { CACHE_ENV, SHOULD_PERSIST_CACHE } from "@/utils/client/configs";
import { client } from "@/utils/client/graphql";
import { pick } from "@/utils/dataCleaning";
import { TIME_SECONDS } from "@/utils/dateTimeUtils";
import { memoize } from "nextjs-better-unstable-cache";

export const callListExams = memoize(
    async ({ slim = false, type = undefined }: { slim?: boolean; type?: string } = {}) => {
        return client
            .query({
                query: slim ? listExamsMetadata : listExams,
                variables: { input: { type } },
                forceUnauth: true,
            })
            .then(({ data }) => data.listExams.items)
            .catch(async e => {
                const { report } = await platform.analytics.logging();
                report(e, "listExams");
                return [] as Exam[];
            });
    },
    {
        persist: SHOULD_PERSIST_CACHE,
        duration: TIME_SECONDS.DAY,
        revalidateTags: ({ slim, type }) => [CACHE_ENV, "list-exams", String(slim), String(type)],
        // log: ["dedupe", "datacache"],
        logid: "list-exams",
    }
);

export const callGetExam = memoize(
    async ({ name }: { name: string }) => {
        return await client
            .query({
                query: getExam,
                variables: { input: { exam: name } },
                forceUnauth: true,
            })
            .then(({ data }) => data.getExam)
            .catch(async error => {
                const { report } = await platform.analytics.logging();
                report(error, "getExam", { exam: name });
                throw error;
            });
    },
    {
        persist: SHOULD_PERSIST_CACHE,
        duration: TIME_SECONDS.DAY,
        revalidateTags: ({ name }) => [CACHE_ENV, "get-exam", name],
        // log: ["dedupe", "datacache"],
        logid: "get-exam",
    }
);

/**
 * returns all the subjects grouped by category
 * @returns Object<{category: {subjects: Array, ...categoryData}}>
 */
export const fetchExamsByType = async () => {
    const exams = await callListExams({ slim: true });
    return groupExamsByType(exams);
};

export const fetchExamsByCategory = async ({ type }: { type: string }) => {
    const exams = await callListExams({ slim: true, type });
    const subjectsByCategory = await fetchSubjectsByCategory();
    return groupExamsByCategory(exams, subjectsByCategory);
};

export const fetchFeaturedExamCategories = async ({ type }: { type: string }) => {
    const examsByCategory = await fetchExamsByCategory({ type });
    return getFeaturedCategories(examsByCategory);
};

export const fetchFeaturedExamTypes = async () => {
    const examsByType = await fetchExamsByType();
    return getFeaturedCategories(pick(examsByType, "AP"));
};
